/**
 * Non-living kinds: tack, trailers, and the general catch-all. These have no
 * health paperwork; a trailer's release gate is its title.
 */

import { z } from 'zod';
import type { LotKindDefinition } from '../registry.js';

export const tack: LotKindDefinition = {
  kind: 'tack',
  label: 'Tack & equipment',
  attributes: z
    .object({
      category: z
        .enum(['saddle', 'bridle', 'harness', 'blanket', 'bit', 'other'])
        .optional(),
      maker: z.string().optional(),
      model: z.string().optional(),
      seatSizeIn: z.number().positive().optional(),
      condition: z.enum(['new', 'excellent', 'good', 'fair']).optional(),
      material: z.string().optional(),
    })
    .strict(),
  releaseChecklist: [],
  facets: [
    { path: 'category', label: 'Category', type: 'enum' },
    { path: 'maker', label: 'Maker', type: 'string' },
    { path: 'condition', label: 'Condition', type: 'enum' },
    { path: 'seatSizeIn', label: 'Seat size', type: 'number', unit: 'in' },
  ],
  details: [
    { path: 'category', label: 'Category', type: 'enum' },
    { path: 'maker', label: 'Maker', type: 'string' },
    { path: 'model', label: 'Model', type: 'string' },
    { path: 'seatSizeIn', label: 'Seat size', type: 'number', unit: 'in' },
    { path: 'material', label: 'Material', type: 'string' },
    { path: 'condition', label: 'Condition', type: 'enum' },
  ],
};

export const trailer: LotKindDefinition = {
  kind: 'trailer',
  label: 'Trailer',
  attributes: z
    .object({
      make: z.string().optional(),
      model: z.string().optional(),
      year: z.number().int().min(1940).max(2100).optional(),
      vin: z.string().optional(),
      titleStatus: z.enum(['clean', 'missing', 'bonded']).optional(),
      lengthFt: z.number().positive().optional(),
      axles: z.number().int().positive().optional(),
      livingQuarters: z.boolean().optional(),
    })
    .strict(),
  releaseChecklist: [
    {
      docType: 'title',
      label: 'Vehicle title',
      required: false,
      requiredWhen: (a) => a['titleStatus'] === 'clean' || a['titleStatus'] === 'bonded',
    },
  ],
  facets: [
    { path: 'make', label: 'Make', type: 'string' },
    { path: 'year', label: 'Year', type: 'number' },
    { path: 'lengthFt', label: 'Length', type: 'number', unit: 'ft' },
    { path: 'livingQuarters', label: 'Living quarters', type: 'boolean' },
  ],
  // A trailer reads year first ("2019 4-Star"), and its title status is the
  // release gate a buyer wants to see; neither is how the sidebar filters.
  details: [
    { path: 'year', label: 'Year', type: 'number' },
    { path: 'make', label: 'Make', type: 'string' },
    { path: 'model', label: 'Model', type: 'string' },
    { path: 'lengthFt', label: 'Length', type: 'number', unit: 'ft' },
    { path: 'axles', label: 'Axles', type: 'number' },
    { path: 'livingQuarters', label: 'Living quarters', type: 'boolean' },
    { path: 'titleStatus', label: 'Title', type: 'enum' },
  ],
};

/** An informational lot: "Read Before Bidding", "Lunch break", "Catalog
 *  coming soon". Occupies a catalog position, never opens for bidding. */
export const notice: LotKindDefinition = {
  kind: 'notice',
  label: 'Notice (no bidding)',
  attributes: z.object({ style: z.enum(['info', 'warning', 'break']).optional() }).strict(),
  releaseChecklist: [],
  facets: [],
};

export const general: LotKindDefinition = {
  kind: 'general',
  label: 'General',
  // Deliberately empty and strict: a lot with no structured story belongs in
  // title + description, not in an attributes junk drawer.
  attributes: z.object({}).strict(),
  releaseChecklist: [],
  facets: [],
};
